import 'swiper/css/bundle'

import { type InfiniteData, useQueryClient } from 'react-query'

import placeholderAvatar from 'assets/images/user_placeholder.png'
import { usePreviewContext } from 'context/previewContext'
import { useTipPost } from 'helpers/hooks'
import { extractMedia } from 'helpers/media'
import {
  type IInfinitePage,
  type IPost,
  type ITipPayload
} from 'types/interfaces/ITypes'

import SinglePostPreviewGoal from './SinglePostPreviewGoal/SinglePostPreviewGoal'
import SinglePostPreviewLive from './SinglePostPreviewLive/SinlgePostPreviewLive'
import SinglePostPreviewMedia from './SinglePostPreviewMedia/SinglePostPreviewMedia'
import SinglePostPreviewPremiumMedia from './SinglePostPreviewMedia/SinglePostPreviewPremiumMedia'
import SinglePostPreviewPoll from './SinglePostPreviewPoll/SinglePostPreviewPoll'
import SinglePostPreviewStory from './SinglePostPreviewStory/SinglePostPreviewStory'
import SinglePostPreviewText from './SinglePostPreviewText/SinglePostPreviewText'

interface Props {
  post: IPost
  selectedSlide?: number
  showControls: boolean
  isFirstPost: boolean
  hasNextPage: boolean
  isInViewport: boolean
  minimizeFn: (_fileIndex: number, _type: string) => void
  toggleControls: () => void
  isLastPost?: boolean
  setIsOptionsNavOpen?: (val: boolean) => void
  onOpenOptions?: () => void
  newPostPage?: boolean
  activeStorySlide?: number | null
  setCurrentStorySlide?: (val: number) => void
  allPosts?: IPost[]
  onClose?: () => void
  setCommentsActive?: (val: boolean) => void
  disablePreviewContextSync?: boolean // FOR POSTMODAL - ANALYTICS - CONTENT
  independentPreviewVisibility?: boolean
  onZoomStateChange?: (zoomed: boolean) => void
}

const SinglePostPreview = ({
  post,
  selectedSlide = 0,
  showControls,
  isFirstPost,
  hasNextPage,
  isInViewport,
  minimizeFn,
  toggleControls,
  isLastPost = false,
  setIsOptionsNavOpen,
  onOpenOptions,
  newPostPage,
  activeStorySlide,
  setCurrentStorySlide,
  allPosts,
  onClose,
  setCommentsActive,
  disablePreviewContextSync = false,
  independentPreviewVisibility = false,
  onZoomStateChange
}: Props) => {
  const queryClient = useQueryClient()
  const { query, selectedPostData } = usePreviewContext()

  const cachedData =
    queryClient.getQueryData<InfiniteData<IInfinitePage>>(query)

  const onTipMutate = (tipAmount: number) => {
    if (cachedData) {
      const newData = {
        ...cachedData,
        pages: cachedData.pages.map(singleFeedPage => {
          return {
            ...singleFeedPage,
            page: {
              ...singleFeedPage.page,
              data: {
                ...singleFeedPage.page.data,
                data: singleFeedPage.page.data.data.map(post =>
                  post.goal && post.id === selectedPostData.id
                    ? {
                        ...post,
                        goal: {
                          ...post.goal,
                          tipped: post.goal?.tipped + tipAmount
                        }
                      }
                    : post
                )
              }
            }
          }
        })
      }

      queryClient.setQueryData(query, newData)
    }
  }

  const onMutateError = () => {
    queryClient.setQueryData(query, cachedData)
  }

  const { setPostTip } = useTipPost({
    onMutate: val => {
      onTipMutate(val)
    },
    onError: onMutateError
  })

  const postFile = {
    name: post.user?.display_name,
    text: post.body,
    mentions: post.tags,
    media: extractMedia(post),
    minimizedName: post.user?.display_name,
    minimizedAvatar:
      post.user?.cropped_avatar?.url ??
      post.user?.avatar?.url ??
      placeholderAvatar,
  video_resume_time: post.video_resume_time,
  video_resume_playing: post.video_resume_playing,
  video_resume_duration: post.video_resume_duration,
  video_resume_progress: post.video_resume_progress,
  video_resume_frame: post.video_resume_frame,
  video_resume_level: post.video_resume_level
  }

  if (post.price && !post.is_purchased) {
    return (
      <SinglePostPreviewPremiumMedia
        post={post}
        selectedSlide={selectedSlide}
        showControls={showControls}
        isFirstPost={isFirstPost}
        minimizeFn={minimizeFn}
        toggleControls={toggleControls}
        hasNextPage={hasNextPage}
        isLastPost={isLastPost}
        setIsOptionsNavOpen={setIsOptionsNavOpen}
        // onOpenOptions={onOpenOptions}
        onClose={onClose}
        isInViewport={isInViewport}
      />
    )
  }

  if (postFile.media.length === 0 && postFile.text) {
    return (
      <SinglePostPreviewText
        post={post}
        showControls={showControls}
        isFirstPost={isFirstPost}
        minimizeFn={minimizeFn}
        toggleControls={toggleControls}
        hasNextPage={hasNextPage}
        isLastPost={isLastPost}
        setIsOptionsNavOpen={setIsOptionsNavOpen}
        onOpenOptions={onOpenOptions}
        onClose={onClose}
      />
    )
  }

  if (postFile.media.length === 0 && post.poll) {
    return (
      <SinglePostPreviewPoll
        post={post}
        showControls={showControls}
        isFirstPost={isFirstPost}
        minimizeFn={minimizeFn}
        toggleControls={toggleControls}
        hasNextPage={hasNextPage}
        isLastPost={isLastPost}
        setIsOptionsNavOpen={setIsOptionsNavOpen}
        // onOpenOptions={onOpenOptions}
      />
    )
  }

  if (postFile.media.length === 0 && post.goal) {
    return (
      <SinglePostPreviewGoal
        post={post}
        showControls={showControls}
        isFirstPost={isFirstPost}
        minimizeFn={minimizeFn}
        toggleControls={toggleControls}
        tipFn={(payload: ITipPayload) => {
          setPostTip.mutate(payload)
        }}
        disabled={setPostTip.isLoading}
        hasNextPage={hasNextPage}
        isLastPost={isLastPost}
        setIsOptionsNavOpen={setIsOptionsNavOpen}
        // onOpenOptions={onOpenOptions}
      />
    )
  }

  if (
    postFile.media.length === 0 &&
    post.stories &&
    !(post.stories.length === 0)
  ) {
    return (
      <SinglePostPreviewStory
        post={post}
        showControls={showControls}
        isFirstPost={isFirstPost}
        minimizeFn={minimizeFn}
        toggleControls={toggleControls}
        hasNextPage={hasNextPage}
        isInViewport={isInViewport}
        isLastPost={isLastPost}
        setIsOptionsNavOpen={setIsOptionsNavOpen}
        activeStorySlide={activeStorySlide}
        setCurrentStorySlide={setCurrentStorySlide}
        allPosts={allPosts}
        // onOpenOptions={onOpenOptions}
      />
    )
  }

  if (postFile.media.length === 0 && post.live) {
    return (
      <SinglePostPreviewLive
        post={post}
        showControls={showControls}
        isFirstPost={isFirstPost}
        minimizeFn={minimizeFn}
        toggleControls={toggleControls}
        hasNextPage={hasNextPage}
        isLastPost={isLastPost}
        setIsOptionsNavOpen={setIsOptionsNavOpen}
      />
    )
  }

  return (
    <SinglePostPreviewMedia
      post={post}
      selectedSlide={selectedSlide}
      showControls={showControls}
        isInViewport={isInViewport}
      isFirstPost={isFirstPost}
      minimizeFn={minimizeFn}
      toggleControls={toggleControls}
      hasNextPage={hasNextPage}
      isLastPost={isLastPost}
      setIsOptionsNavOpen={setIsOptionsNavOpen}
      newPostPage={newPostPage}
      onOpenOptions={onOpenOptions}
      onClose={onClose}
      setCommentsActive={setCommentsActive}
      disablePreviewContextSync={disablePreviewContextSync}
      independentPreviewVisibility={independentPreviewVisibility}
      onZoomStateChange={onZoomStateChange}
    />
  )
}

export default SinglePostPreview